import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { QueryEmptyState } from '../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { StatCard } from '../components/dashboard/StatCard';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { apiErrorMessage, formatTime } from '../i18n';
import { api } from '../lib/api';
import { formatCount } from '../lib/dashboard/formatCount';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import { NewWallDrawer, type NewWallDraft } from './video-walls/NewWallDrawer';
import { WallCanvasCard } from './video-walls/WallCanvasCard';
import { WallHealthCard } from './video-walls/WallHealthCard';
import { WallPublishCard } from './video-walls/WallPublishCard';
import { WallsListCard } from './video-walls/WallsListCard';
import { manifestLabel } from './video-walls/wallFormat';
import { videoWallDetailQuery, videoWallsQuery } from './video-walls/wallQueries';

/**
 * GhostScreens Canvas UI — matches the product mock: stats, active canvas,
 * publish stepper, health, wall list, and New wall drawer (ADR 0007).
 */
export function VideoWallsPage() {
  const { t } = useTranslation('videoWalls');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [selectedWallId, setSelectedWallId] = useState<string | null>(null);
  const [mediaAssetId, setMediaAssetId] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (location.hash === '#new') setDrawerOpen(true);
  }, [location.hash]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerError(null);
    if (location.hash === '#new') {
      navigate({ pathname: '/video-walls', search: location.search }, { replace: true });
    }
  };

  const wallsQuery = useQuery(videoWallsQuery(organisationId));
  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });
  const mediaQuery = useQuery({
    queryKey: ['media', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
  });

  const walls = wallsQuery.data ?? [];
  const effectiveWallId = selectedWallId ?? walls[0]?.id ?? null;

  const detailQuery = useQuery(videoWallDetailQuery(organisationId, effectiveWallId));

  const retry = useQueryRetry(async () => {
    await Promise.all([
      wallsQuery.refetch(),
      screensQuery.refetch(),
      locationsQuery.refetch(),
      mediaQuery.refetch(),
      detailQuery.refetch(),
    ]);
  });

  const invalidateWalls = async (wallId?: string) => {
    await queryClient.invalidateQueries({ queryKey: ['video-walls', organisationId] });
    if (wallId) {
      await queryClient.invalidateQueries({ queryKey: ['video-wall', organisationId, wallId] });
    }
  };

  const createWall = useMutation({
    mutationFn: async (draft: NewWallDraft) => {
      const wall = await api.createVideoWall(organisationId, {
        name: draft.name,
        rows: draft.rows,
        cols: draft.cols,
        mode: draft.mode,
        bezelHorizontalMm: draft.bezelHorizontalMm,
        bezelVerticalMm: draft.bezelHorizontalMm,
        panelWidthMm: null,
        panelHeightMm: null,
      });

      let index = 0;
      for (let row = 0; row < draft.rows; row += 1) {
        for (let col = 0; col < draft.cols; col += 1) {
          const screenId = draft.screenIds[index];
          index += 1;
          if (!screenId) continue;
          await api.assignVideoWallTile(organisationId, wall.id, { screenId, row, col });
        }
      }

      await api.publishVideoWall(organisationId, wall.id, {
        mediaAssetId: draft.mediaAssetId,
      });
      return wall;
    },
    onSuccess: async (wall) => {
      setDrawerError(null);
      closeDrawer();
      setSelectedWallId(wall.id);
      await invalidateWalls(wall.id);
    },
    onError: (error) => setDrawerError(apiErrorMessage(error, t('page.createError'))),
  });

  const updateWall = useMutation({
    mutationFn: (input: {
      mode?: 'extend' | 'mirror';
      bezelHorizontalMm?: number;
      bezelVerticalMm?: number;
    }) => {
      if (!effectiveWallId) throw new Error('No wall selected');
      return api.updateVideoWall(organisationId, effectiveWallId, input);
    },
    onSuccess: async () => {
      await invalidateWalls(effectiveWallId ?? undefined);
    },
    onError: (error) => setMessage(apiErrorMessage(error, t('page.updateError'))),
  });

  const assignTile = useMutation({
    mutationFn: (input: { row: number; col: number; screenId: string }) => {
      if (!effectiveWallId) throw new Error('No wall selected');
      return api.assignVideoWallTile(organisationId, effectiveWallId, input);
    },
    onSuccess: async () => {
      await invalidateWalls(effectiveWallId ?? undefined);
    },
    onError: (error) => setMessage(apiErrorMessage(error, t('page.assignError'))),
  });

  const clearTile = useMutation({
    mutationFn: (input: { row: number; col: number }) => {
      if (!effectiveWallId) throw new Error('No wall selected');
      return api.clearVideoWallTile(organisationId, effectiveWallId, input.row, input.col);
    },
    onSuccess: async () => {
      await invalidateWalls(effectiveWallId ?? undefined);
    },
  });

  const publish = useMutation({
    mutationFn: () => {
      if (!effectiveWallId || !mediaAssetId) throw new Error('Missing wall or asset');
      return api.publishVideoWall(organisationId, effectiveWallId, { mediaAssetId });
    },
    onSuccess: async () => {
      setMessage(null);
      await invalidateWalls(effectiveWallId ?? undefined);
    },
    onError: (error) => setMessage(apiErrorMessage(error, t('page.publishError'))),
  });

  const stop = useMutation({
    mutationFn: () => {
      if (!effectiveWallId) throw new Error('No wall selected');
      return api.stopVideoWall(organisationId, effectiveWallId);
    },
    onSuccess: async () => {
      await invalidateWalls(effectiveWallId ?? undefined);
    },
  });

  const [occupiedScreenIds, setOccupiedScreenIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!drawerOpen || walls.length === 0) {
      if (!drawerOpen) setOccupiedScreenIds(new Set());
      return;
    }
    let cancelled = false;
    void (async () => {
      const details = await Promise.all(
        walls.map((wall) => api.getVideoWall(organisationId, wall.id).catch(() => null)),
      );
      if (cancelled) return;
      const occupied = new Set<string>();
      for (const detail of details) {
        for (const tile of detail?.tiles ?? []) occupied.add(tile.screenId);
      }
      setOccupiedScreenIds(occupied);
    })();
    return () => {
      cancelled = true;
    };
  }, [drawerOpen, walls, organisationId]);

  const readyMedia = useMemo(
    () => (mediaQuery.data ?? []).filter((asset) => asset.status === 'ready'),
    [mediaQuery.data],
  );

  const selectedDetail = detailQuery.data ?? null;
  const expectedTiles = selectedDetail ? selectedDetail.rows * selectedDetail.cols : 0;
  const assignedTiles = selectedDetail?.tiles.length ?? 0;

  const ops = selectedDetail?.operations ?? null;
  const onlineTiles = {
    online: ops?.onlineTileCount ?? 0,
    total: ops?.requiredTileCount ?? 0,
  };

  const extendCount = walls.filter((wall) => wall.mode === 'extend').length;
  const mirrorCount = walls.filter((wall) => wall.mode === 'mirror').length;
  const publication = selectedDetail?.activePublication ?? null;
  const readyNote = publication
    ? t('stats.ready.note', {
        ready: ops?.liveReadyTileCount ?? 0,
        total: ops?.requiredTileCount ?? 0,
      })
    : t('stats.ready.noteNone');

  const loading =
    wallsQuery.isPending ||
    screensQuery.isPending ||
    mediaQuery.isPending ||
    locationsQuery.isPending;
  const failed =
    wallsQuery.isError || screensQuery.isError || mediaQuery.isError || locationsQuery.isError;

  return (
    <div className="space-y-6">
      <StatsGrid columns={4}>
        <StatCard
          label={t('stats.walls.label')}
          value={formatCount(walls.length, wallsQuery.isPending)}
          note={
            walls.length
              ? t('stats.walls.note', { extend: extendCount, mirror: mirrorCount })
              : t('stats.walls.noteEmpty')
          }
        />
        <StatCard
          label={t('stats.tilesOnline.label')}
          value={
            selectedDetail
              ? t('stats.fraction', {
                  value: onlineTiles.online,
                  total: onlineTiles.total || expectedTiles,
                })
              : formatCount(0, wallsQuery.isPending)
          }
          note={
            selectedDetail ? t('stats.tilesOnline.noteSelected') : t('stats.tilesOnline.noteNone')
          }
          tone={
            selectedDetail && onlineTiles.total > 0 && onlineTiles.online === onlineTiles.total
              ? 'ok'
              : 'default'
          }
        />
        <StatCard
          label={t('stats.ready.label')}
          value={
            publication
              ? t('stats.fraction', {
                  value: ops?.liveReadyTileCount ?? 0,
                  total: ops?.requiredTileCount ?? 0,
                })
              : '—'
          }
          note={readyNote}
          tone={
            publication &&
            ops != null &&
            ops.requiredTileCount > 0 &&
            ops.liveReadyTileCount >= ops.requiredTileCount
              ? 'ok'
              : 'default'
          }
        />
        <StatCard
          label={t('stats.manifest.label')}
          value={manifestLabel(publication)}
          note={
            publication?.epochStartAt
              ? t('stats.manifest.noteLive', { time: formatTime(publication.epochStartAt) })
              : publication
                ? t(`publicationStatus.${publication.status}`)
                : t('stats.manifest.noteDraft')
          }
        />
      </StatsGrid>

      {failed || retry.isRetrying ? (
        <QueryErrorState
          message={t('page.loadError')}
          onRetry={retry.retry}
          isRetrying={retry.isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock />
      ) : (
        <div
          className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(280px,400px)]"
          style={{ animation: 'gs-in 260ms ease both' }}
        >
          <div className="space-y-5">
            {!selectedDetail ? (
              <QueryEmptyState>{t('page.empty')}</QueryEmptyState>
            ) : detailQuery.isPending ? (
              <QueryLoadingBlock />
            ) : detailQuery.isError ? (
              <QueryErrorState
                message={t('page.wallLoadError')}
                onRetry={() => void detailQuery.refetch()}
              />
            ) : (
              <>
                <WallCanvasCard
                  wall={selectedDetail}
                  screens={screensQuery.data ?? []}
                  mediaAssets={readyMedia}
                  mediaAssetId={mediaAssetId}
                  onMediaAssetIdChange={setMediaAssetId}
                  busy={updateWall.isPending || assignTile.isPending}
                  onModeChange={(mode) => updateWall.mutate({ mode })}
                  onBezelChange={(mm) =>
                    updateWall.mutate({ bezelHorizontalMm: mm, bezelVerticalMm: mm })
                  }
                  onAssignTile={(cell, screenId) => assignTile.mutate({ ...cell, screenId })}
                  onClearTile={(cell) => clearTile.mutate(cell)}
                />
                <WallPublishCard
                  publication={publication}
                  operations={selectedDetail?.operations ?? null}
                  mode={selectedDetail?.mode ?? null}
                  canPublish={
                    Boolean(mediaAssetId) && assignedTiles === expectedTiles && expectedTiles > 0
                  }
                  busy={publish.isPending || stop.isPending}
                  onPublish={() => publish.mutate()}
                  onStop={() => stop.mutate()}
                  onRefresh={() => void detailQuery.refetch()}
                  updatedAt={detailQuery.dataUpdatedAt || null}
                />
              </>
            )}
            {message ? (
              <p className="text-sm" style={{ color: 'var(--gs-muted)' }}>
                {message}
              </p>
            ) : null}
          </div>

          <div className="space-y-5">
            <WallHealthCard wall={selectedDetail} screens={screensQuery.data ?? []} />
            <WallsListCard
              walls={walls}
              selectedId={effectiveWallId}
              onSelect={setSelectedWallId}
              onNewWall={() => {
                setDrawerError(null);
                setDrawerOpen(true);
                navigate('/video-walls#new');
              }}
            />
          </div>
        </div>
      )}

      <NewWallDrawer
        open={drawerOpen}
        busy={createWall.isPending}
        error={drawerError}
        screens={screensQuery.data ?? []}
        locations={locationsQuery.data ?? []}
        mediaAssets={readyMedia}
        occupiedScreenIds={occupiedScreenIds}
        onClose={closeDrawer}
        onCreate={(draft) => createWall.mutate(draft)}
      />
    </div>
  );
}
